/**
 * FormSelect — the compact, shadcn-backed select used across the Style File
 * form rows (QC defects, production stage/delay, costing line kind, dispatch
 * route). Built on the Radix `Select` primitive — we never reach for a native
 * `<select>`, whose OS-native dropdown breaks the Flint design language.
 *
 * Radix forbids an empty-string item value, so an option whose `value` is the
 * empty string is mapped to a private sentinel and back transparently — callers
 * keep using "" for the "none" choice.
 *
 * @see packages/ui/src/primitives/select.tsx
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";

export interface FormSelectOption {
  value: string;
  label: string;
}

/** Radix bans value=""; map "" ↔ this sentinel so callers keep using "". */
const EMPTY = " empty";

export function FormSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  ariaLabel,
  triggerClassName = "h-8 px-2 text-[12px]",
  itemClassName = "text-[12px]",
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: ReadonlyArray<FormSelectOption>;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  triggerClassName?: string;
  itemClassName?: string;
}) {
  return (
    <Select
      value={value === "" ? EMPTY : value}
      onValueChange={(v) => onValueChange(v === EMPTY ? "" : v)}
      disabled={disabled ?? false}
    >
      <SelectTrigger aria-label={ariaLabel} className={triggerClassName}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => {
          const v = o.value === "" ? EMPTY : o.value;
          return (
            <SelectItem key={v} value={v} className={itemClassName}>
              {o.label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
